import axios, {AxiosError, AxiosInstance, AxiosResponse} from "axios"
import {EnabledProductRef, ProductList, ProductsUpdateBody, StoreInfo, UserAccount} from "@/services/models.types"
import {ApiError} from "@/services/ApiError"
import {attachLoggerInterceptor} from "@/utils/logging"
import {appConfig} from "@/config/app.config"

class ApiService {

    private readonly axiosInstance: AxiosInstance

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: appConfig.API.SPCI_BASE_URL,
            headers: {
                "Accept": "application/json, application/pdf",
                "Content-Type": "application/json",
                "Source": "spci",
            },
        })

        attachLoggerInterceptor(this.axiosInstance)
    }

    public setAuthenticationToken(token: string) {
        this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`
    }

    public fetchUserAccount(): Promise<UserAccount> {
        return this.axiosInstance.get("account")
            .then((response: AxiosResponse<UserAccount>) => response.data)
            .catch((e) => Promise.reject(this.extractError(e)))
    }

    public fetchStoreInfo(storeId: number): Promise<StoreInfo> {
        return this.axiosInstance.get(`stores/${storeId}`)
            .then((response: AxiosResponse<StoreInfo>) => ({...response.data, storeId}))
            .catch((e) => Promise.reject(this.extractError(e)))
    }

    public fetchStoreEnabledProducts(storeId: number): Promise<EnabledProductRef[]> {
        return this.axiosInstance.get(`stores/${storeId}/products/enabled`)
            .then((response: AxiosResponse<EnabledProductRef[]>) => response.data)
            .catch((e) => Promise.reject(this.extractError(e)))
    }

    public fetchProductList(listId: string): Promise<ProductList> {
        return this.axiosInstance.get(`products/productLists/${listId}`)
            .then((response: AxiosResponse<ProductList>) => response.data)
            .catch((e) => Promise.reject(this.extractError(e)))
    }

    public updateProducts(storeId: number, productsUpdate: ProductsUpdateBody): Promise<void> {
        return this.axiosInstance.post(`stores/${storeId}/products/enabled`, productsUpdate)
            .then((response: AxiosResponse<void>) => response.data)
            .catch((e) => Promise.reject(this.extractError(e)))
    }

    public fetchActiveListProductsPdfData(storeId: string, listId: string): Promise<Blob> {
        return this.axiosInstance.get(`stores/${storeId}/productLists/${listId}/pdf`, { responseType: "arraybuffer"})
            .then((response: AxiosResponse<ArrayBuffer>) => new Blob([response.data], { type: "application/pdf" }))
            .catch((e) => Promise.reject(this.extractError(e)))
    }

    public fetchActiveCategoryProductsPdfData(storeId: string, category: string): Promise<Blob> {
        return this.axiosInstance.get(`stores/${storeId}/categories/${category}/pdf`, { responseType: "arraybuffer"})
            .then((response: AxiosResponse<ArrayBuffer>) => new Blob([response.data], { type: "application/pdf" }))
            .catch((e) => Promise.reject(this.extractError(e)))
    }

    private extractError(e: AxiosError<{ errorMsg?: string }>): Error {
        const response = e.response
        if (response && response.data && response.data.errorMsg) {
            return new ApiError(`Request failed with HTTP status code ${response.status}: ${JSON.stringify(response.data)} `,
                response.data.errorMsg)
        } else {
            return e
        }
    }
}

const apiService = new ApiService()
export default apiService
